/**
 * useCourtage.ts — TanStack Query hooks for the Courtage area (Story UI-U5).
 *
 * live mode → the real GET /commission/* reads via the feature api wrapper.
 * mock mode → the deterministic fixtures (getDataMode(), the V10-J pattern —
 *             mock is the dev/test default; the mock layer is NOT deleted).
 *
 * The two decisions ride the EXISTING audited E11 endpoints (override/transition);
 * their mutations invalidate the statement list + detail so the peek + list refresh.
 *
 * [Source: apps/web/src/features/invoices/hooks/useInvoiceRows.ts] (hook + getDataMode pattern)
 * [Source: docs/stories/ui-u5-courtage-tarifmatrix.md#Tasks — Task 5]
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import {
  fetchAkonto,
  fetchExpectations,
  fetchKennzahlen,
  fetchSplits,
  fetchStatement,
  fetchStatements,
  overrideLine,
  transitionLine,
  type PeriodBounds,
  type StatementListFilters,
} from '../api/courtage-api';
import {
  COURTAGE_AKONTO_MOCK,
  COURTAGE_EXPECTATIONS_MOCK,
  COURTAGE_KENNZAHLEN_MOCK,
  COURTAGE_SPLITS_MOCK,
  COURTAGE_STATEMENT_DETAILS_MOCK,
  COURTAGE_STATEMENTS_MOCK,
} from '../mock';
import type {
  AkontoBalanceViewDto,
  CommissionExpectationViewDto,
  CommissionLineStatus,
  CommissionSplitViewDto,
  CommissionStatementDetailDto,
  CommissionStatementListItemDto,
  MatchLevel,
  ReconciliationKennzahlenDto,
} from '@brokly/shared';

const modeKey = (): string => (isLiveData() ? 'live' : 'mock');

/** GET /commission/statements — the Abrechnungen list. */
export function useCourtageStatements(filters: StatementListFilters = {}) {
  const live = isLiveData();
  return useQuery<CommissionStatementListItemDto[]>({
    queryKey: qk.courtageStatements({ mode: modeKey(), ...filters }),
    queryFn: () => (live ? fetchStatements(filters) : Promise.resolve(COURTAGE_STATEMENTS_MOCK)),
    ...(live ? {} : { initialData: COURTAGE_STATEMENTS_MOCK, staleTime: Infinity }),
  });
}

/** GET /commission/statements/:id — the peek detail (enabled only when an id is set). */
export function useCourtageStatement(id: string | null) {
  const live = isLiveData();
  return useQuery<CommissionStatementDetailDto>({
    queryKey: qk.courtageStatement(id ?? 'none'),
    enabled: id !== null,
    queryFn: () =>
      live
        ? fetchStatement(id!)
        : Promise.resolve(
            COURTAGE_STATEMENT_DETAILS_MOCK[id!] ?? {
              ...COURTAGE_STATEMENTS_MOCK[0]!,
              broker_vu_code: null,
              broker_finma_nr: null,
              total_amount: null,
              sum_lines_ist: null,
              kontrollsumme_ok: true,
              lines: [],
              correction_groups: [],
            },
          ),
  });
}

/** GET /commission/akonto — the running Akonto-Salden. */
export function useCourtageAkonto() {
  const live = isLiveData();
  return useQuery<AkontoBalanceViewDto[]>({
    queryKey: qk.courtageAkonto(),
    queryFn: () => (live ? fetchAkonto() : Promise.resolve(COURTAGE_AKONTO_MOCK)),
    ...(live ? {} : { initialData: COURTAGE_AKONTO_MOCK, staleTime: Infinity }),
  });
}

/** GET /commission/splits — the co-broker split rows. */
export function useCourtageSplits() {
  const live = isLiveData();
  return useQuery<CommissionSplitViewDto[]>({
    queryKey: qk.courtageSplits(),
    queryFn: () => (live ? fetchSplits() : Promise.resolve(COURTAGE_SPLITS_MOCK)),
    ...(live ? {} : { initialData: COURTAGE_SPLITS_MOCK, staleTime: Infinity }),
  });
}

/** GET /commission/reconciliation/kennzahlen — Monitoring KPI tiles. */
export function useCourtageKennzahlen(period: PeriodBounds) {
  const live = isLiveData();
  const key = `${period.period_from}..${period.period_to}`;
  return useQuery<ReconciliationKennzahlenDto>({
    queryKey: qk.courtageKennzahlen(key),
    queryFn: () => (live ? fetchKennzahlen(period) : Promise.resolve(COURTAGE_KENNZAHLEN_MOCK)),
    ...(live ? {} : { initialData: COURTAGE_KENNZAHLEN_MOCK, staleTime: Infinity }),
  });
}

/** GET /commission/expectations — the Erwartungen for the selected month. */
export function useCourtageExpectations(period: PeriodBounds) {
  const live = isLiveData();
  const key = `${period.period_from}..${period.period_to}`;
  return useQuery<CommissionExpectationViewDto[]>({
    queryKey: qk.courtageExpectations(key),
    queryFn: () => (live ? fetchExpectations(period) : Promise.resolve(COURTAGE_EXPECTATIONS_MOCK)),
    ...(live ? {} : { initialData: COURTAGE_EXPECTATIONS_MOCK, staleTime: Infinity }),
  });
}

// ── Decisions (EXISTING audited E11 endpoints) ──────────────────────────────

export interface TransitionArgs {
  lineId: string;
  toStatus: CommissionLineStatus;
  reason?: string;
}

export interface OverrideArgs {
  lineId: string;
  reason: string;
  newStatus?: CommissionLineStatus;
  newMatchLevel?: MatchLevel;
}

/** POST /admin/commission-exception/:lineId/transition — invalidates the statement queries. */
export function useTransitionLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lineId, toStatus, reason }: TransitionArgs) =>
      transitionLine(lineId, { toStatus, ...(reason !== undefined ? { reason } : {}) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['courtage', 'statements'] });
      void qc.invalidateQueries({ queryKey: ['courtage', 'statement'] });
    },
  });
}

/** POST /admin/commission-exception/:lineId/override — mandatory reason; invalidates queries. */
export function useOverrideLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lineId, reason, newStatus, newMatchLevel }: OverrideArgs) =>
      overrideLine(lineId, {
        reason,
        ...(newStatus !== undefined ? { newStatus } : {}),
        ...(newMatchLevel !== undefined ? { newMatchLevel } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['courtage', 'statements'] });
      void qc.invalidateQueries({ queryKey: ['courtage', 'statement'] });
    },
  });
}
